import type * as React from 'react';
import { cn } from '../../lib/cn.ts';
import { ChatMarkdown } from './chat-markdown.tsx';

export interface ChatRecapCardProps extends Omit<
  React.ComponentProps<'div'>,
  'style' | 'children'
> {
  /** What this is and when it was, e.g. "Samenvatting vorig gesprek — 28 mei". */
  title: string;
  /** The summary itself, as it was written. */
  text: string;
  /**
   * The way to the conversation this summarises.
   *
   * A node rather than an address: the summary is about a page of this same
   * app, so following it must not reload everything — on a routed page the
   * element belongs to the router, and only the caller can make one. It carries
   * the look through `chatCardLinkVariants`.
   */
  link: React.ReactNode;
}

/**
 * What was said last time, put in the drawer for whoever opens the meeting.
 *
 * A heading rather than bold text, so somebody moving through the conversation
 * by structure finds it, and the way to the verslag it was read from under it
 * — the summary is a paragraph, and the conversation behind it is the whole
 * thing.
 */
export function ChatRecapCard({
  title,
  text,
  link,
  className,
  ...props
}: ChatRecapCardProps) {
  return (
    <div
      data-slot='chat-recap-card'
      className={cn('flex flex-col', className)}
      {...props}
    >
      <h3 className='text-md font-bold'>{title}</h3>
      <ChatMarkdown className='mt-1'>{text}</ChatMarkdown>
      <div className='mt-2 self-start'>{link}</div>
    </div>
  );
}
